import type {
  SupplierRespondMutationOptions,
  SupplierRespondVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { supplierRespond } from './supplierRespond';

/**
 * Mutation key for supplier respond actions.
 * Used with useMutationState to track pending supplier actions.
 */
export const MUTATION_KEY_SUPPLIER_RESPOND = [
  'calloffs',
  'supplierRespond',
] as const;

/**
 * Custom hook for submitting a supplier action response for a call-off via magic link.
 * Performs state transitions based on the selected action:
 * - confirm: Accept the call-off as-is → CONFIRMED
 * - confirm_with_changes: Accept with proposed modifications → SUPPLIER_CHANGE_REQUESTED
 * - decline: Reject the call-off → DECLINED
 * - request_cancel: Request cancellation (from CONFIRMED state) → CANCEL_REQUESTED
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link-(Public)/operation/supplier_respond_calloffs_magic_link__calloff_id__respond_post
 */
export const useMutationSupplierRespond = (
  options?: SupplierRespondMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MUTATION_KEY_SUPPLIER_RESPOND,
    mutationFn: async ({
      callOffId,
      token,
      payload,
    }: SupplierRespondVariables) => supplierRespond(callOffId, token, payload),
    ...options,
    onSuccess(data, variables, context) {
      // Invalidate the call-off via magic link query
      void queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getViaMagicLink(
          variables.callOffId,
          variables.token,
        ),
      });

      // Call the user-provided onSuccess callback if any
      options?.onSuccess?.(data, variables, context);
    },
  });
};
